import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Star, Send, MessageSquare, TrendingUp, AlertTriangle, X, Phone, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  REVIEWS — admin dashboard for the Review Request Automation system
// ═══════════════════════════════════════════════════════════════════

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const ReviewsPage = ({ API_BASE, currentUser }) => {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, a, p] = await Promise.all([
        axios.get(`${API_BASE}/reviews`),
        axios.get(`${API_BASE}/reviews/analytics`),
        axios.get(`${API_BASE}/patients`).catch(() => ({ data: [] })),
      ]);
      setList(l.data || []); setStats(a.data); setPatients(p.data || []);
    } catch { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return list;
    if (filter === 'pending')  return list.filter(r => !r.rating);
    if (filter === 'positive') return list.filter(r => r.rating >= 4);
    if (filter === 'neutral')  return list.filter(r => r.rating === 3);
    if (filter === 'negative') return list.filter(r => r.rating && r.rating < 3);
    return list;
  }, [list, filter]);

  const sendReview = async () => {
    if (!sendTo) return;
    setSending(true); setSentResult(null);
    try {
      const r = await axios.post(`${API_BASE}/reviews/send`, { patientId: sendTo });
      setSentResult(r.data);
      load();
    } catch (e) { showMsg('error', 'Send failed: ' + (e.response?.data?.error || e.message)); }
    setSending(false);
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); showMsg('success', 'Link copied'); } catch { alert('Copy failed: ' + url); }
  };

  const dismiss = async (id) => {
    await axios.post(`${API_BASE}/reviews/${id}/dismiss`).then(load);
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Star size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Reviews & Feedback</h2>
        <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
          {stats?.last90Days?.sent || 0} sent · {stats?.last90Days?.responded || 0} responded ({stats?.last90Days?.responseRate || 0}%) · avg: <strong>{stats?.last90Days?.avgRating || '—'}</strong>★
        </p>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`, fontWeight: 600, fontSize: 13.5 }}>{msg.text}</div>
      )}

      {/* Rating breakdown + Send new */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 14 }}>
        <div style={{ ...cardStyle, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>Rating Breakdown (90 days)</div>
          {[5, 4, 3, 2, 1].map(n => {
            const cnt = stats?.last90Days?.byRating?.[n] || 0;
            const total = stats?.last90Days?.responded || 1;
            const pct = Math.round((cnt / total) * 100);
            return (
              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: theme.navy, minWidth: 20 }}>{n}★</span>
                <div style={{ flex: 1, background: theme.borderLight, height: 10, borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: n >= 4 ? '#16a34a' : n === 3 ? '#f59e0b' : '#dc2626' }} />
                </div>
                <span style={{ fontSize: 11, color: theme.textMuted, minWidth: 50, textAlign: 'right' }}>{cnt} ({pct}%)</span>
              </div>
            );
          })}
        </div>

        <div style={{ ...cardStyle, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>Send New Review Request</div>
          <select value={sendTo} onChange={e => setSendTo(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }}>
            <option value="">Select patient…</option>
            {patients.slice(0, 200).map(p => (
              <option key={p._id} value={p._id}>{p.name} ({p.phone || 'no phone'})</option>
            ))}
          </select>
          <button onClick={sendReview} disabled={!sendTo || sending} style={{ ...btnStyle, marginTop: 0, width: '100%' }}>
            {sending ? 'Generating…' : <><Send size={13} style={{ marginRight: 6, verticalAlign: -2 }} /> Generate Review Link</>}
          </button>

          {sentResult && (
            <div style={{ marginTop: 10, padding: 10, background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
              {sentResult.alreadySent && <div style={{ fontSize: 12, color: '#92400e', marginBottom: 4 }}>⚠ Already sent in last 24h — reusing link.</div>}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <input readOnly value={sentResult.url} style={{ ...inputStyle, flex: 1, minWidth: 160, fontSize: 11, fontFamily: 'monospace' }} />
                <button onClick={() => copyLink(sentResult.url)} style={smallBtn}><Copy size={11} /></button>
                <a href={sentResult.waLink} target="_blank" rel="noreferrer" style={{ ...smallBtn, background: '#25D366', color: 'white', borderColor: '#25D366', textDecoration: 'none' }}>
                  <ExternalLink size={11} /> WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {[
          { k: 'all', l: 'All', c: list.length },
          { k: 'pending', l: 'Pending', c: list.filter(r => !r.rating).length },
          { k: 'positive', l: '★ 4-5', c: list.filter(r => r.rating >= 4).length, color: '#16a34a' },
          { k: 'neutral', l: '★ 3', c: list.filter(r => r.rating === 3).length, color: '#f59e0b' },
          { k: 'negative', l: '★ 1-2', c: list.filter(r => r.rating && r.rating < 3).length, color: '#dc2626' },
        ].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${filter === f.k ? (f.color || theme.primary) : theme.border}`,
            background: filter === f.k ? (f.color || theme.primary) + '18' : 'white',
            color: filter === f.k ? (f.color || theme.primary) : theme.textMuted,
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
          }}>{f.l} <span style={{ marginLeft: 4, background: 'rgba(0,0,0,0.08)', padding: '1px 6px', borderRadius: 999, fontSize: 10 }}>{f.c}</span></button>
        ))}
      </div>

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 30 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 50 }}>
          <Star size={40} color={theme.textSubtle} style={{ marginBottom: 8, opacity: 0.5 }} />
          <p style={{ color: theme.textMuted }}>Koi review {filter !== 'all' ? 'is category me' : ''} nahi</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filtered.map(r => {
            const color = !r.rating ? '#94a3b8' : r.rating >= 4 ? '#16a34a' : r.rating === 3 ? '#f59e0b' : '#dc2626';
            return (
              <div key={r._id} style={{ ...cardStyle, padding: 12, borderLeft: `4px solid ${color}` }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 60, textAlign: 'center' }}>
                    {r.rating ? (
                      <>
                        <div style={{ fontSize: 22, fontWeight: 800, color }}>{r.rating}★</div>
                        <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', fontWeight: 700 }}>{r.sentiment}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.5 }}>⏳ PENDING</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: theme.navy }}>{r.patientName}</div>
                    <div style={{ fontSize: 11.5, color: theme.textMuted }}>
                      <Phone size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{r.patientPhone || '—'}
                      {' · '}Sent: {fmt(r.sentAt)}
                      {r.respondedAt && ' · Responded: ' + fmt(r.respondedAt)}
                    </div>
                    {r.feedback && (
                      <div style={{ marginTop: 6, padding: '6px 10px', background: theme.bg, borderRadius: 6, fontSize: 13, color: theme.navy, fontStyle: 'italic' }}>"{r.feedback}"</div>
                    )}
                    {r.redirectedToGoogle && <div style={{ marginTop: 4, fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✓ Redirected to Google Reviews</div>}
                  </div>
                  {r.rating && r.rating < 3 && !r.status === 'dismissed' && (
                    <button onClick={() => dismiss(r._id)} style={{ ...smallBtn, color: theme.danger }}>
                      <X size={11} /> Dismiss
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '5px 9px', borderRadius: 5, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 11, fontWeight: 600 };

export default ReviewsPage;
